"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectItem } from "@/components/ui/select";
import { INTEGRATIONS } from "@/lib/integration-config";
import { useSubscription } from "@/hooks/useSubscription";
import type { CampaignFormData, MailchimpAudience, TenantColors } from "./types";

interface MeetupGroup {
  id: string;
  name: string;
  urlname: string;
  city: string;
  state: string;
  memberCount: number;
  logo?: string;
}

interface MeetupEventLink {
  id: string;
  meetupGroupUrlname: string;
  meetupEventId: string;
  lastSyncedAt: string | null;
}

interface LumaCalendar {
  api_id: string;
  name: string;
  slug: string;
  description?: string;
  geo_city?: string;
  geo_region?: string;
}

interface LumaEventLink {
  id: string;
  lumaCalendarId: string;
  lumaEventId: string;
  lastSyncedAt: string | null;
}

interface IntegrationsStepProps {
  campaignData: CampaignFormData;
  onCampaignChange: (field: keyof CampaignFormData, value: string | boolean) => void;
  audiences: MailchimpAudience[];
  audiencesLoading: boolean;
  audiencesError: string | null;
  onRefetchAudiences: () => void;
  tenantColors?: TenantColors | null;
  campaignId?: string | null;
  isEditing?: boolean;
}

const PLAN_LEVELS: Record<string, number> = { FREE: 0, STARTER: 1, PREMIUM: 2 };

export function IntegrationsStep({
  campaignData,
  onCampaignChange,
  audiences,
  audiencesLoading,
  audiencesError,
  onRefetchAudiences,
  tenantColors,
  campaignId,
  isEditing,
}: IntegrationsStepProps) {
  const { subscription } = useSubscription();
  const currentPlan = subscription?.plan || "FREE";
  const currentPlanLevel = PLAN_LEVELS[currentPlan] || 0;

  const mailchimpConfig = INTEGRATIONS.mailchimp;
  const mailchimpRequiredLevel = mailchimpConfig.requiredPlan ? PLAN_LEVELS[mailchimpConfig.requiredPlan] || 0 : 0;
  const isMailchimpLocked = mailchimpRequiredLevel > currentPlanLevel;

  const meetupConfig = INTEGRATIONS.meetup;
  const meetupRequiredLevel = meetupConfig.requiredPlan ? PLAN_LEVELS[meetupConfig.requiredPlan] || 0 : 0;
  const isMeetupLocked = meetupRequiredLevel > currentPlanLevel;

  const secondaryButtonStyle = tenantColors
    ? { backgroundColor: tenantColors.secondaryColor, borderColor: tenantColors.secondaryColor, color: '#FFFFFF' }
    : undefined;

  const lumaConfig = INTEGRATIONS.luma;
  const lumaRequiredLevel = lumaConfig.requiredPlan ? PLAN_LEVELS[lumaConfig.requiredPlan] || 0 : 0;
  const isLumaLocked = lumaRequiredLevel > currentPlanLevel;

  const [meetupConnected, setMeetupConnected] = useState(false);
  const [meetupGroups, setMeetupGroups] = useState<MeetupGroup[]>([]);
  const [meetupGroupsLoading, setMeetupGroupsLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [existingLinks, setExistingLinks] = useState<MeetupEventLink[]>([]);
  const [meetupSyncing, setMeetupSyncing] = useState(false);
  const [meetupMessage, setMeetupMessage] = useState("");
  const [meetupError, setMeetupError] = useState("");

  const [lumaConnected, setLumaConnected] = useState(false);
  const [lumaCalendars, setLumaCalendars] = useState<LumaCalendar[]>([]);
  const [lumaCalendarsLoading, setLumaCalendarsLoading] = useState(false);
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);
  const [existingLumaLinks, setExistingLumaLinks] = useState<LumaEventLink[]>([]);
  const [lumaSyncing, setLumaSyncing] = useState(false);
  const [lumaMessage, setLumaMessage] = useState("");
  const [lumaError, setLumaError] = useState("");

  const loadMeetupGroups = useCallback(async () => {
    setMeetupGroupsLoading(true);
    try {
      const res = await fetch('/api/meetup/groups');
      const data = await res.json();
      setMeetupConnected(data.connected);
      setMeetupGroups(data.groups || []);
    } catch {
      setMeetupConnected(false);
    } finally {
      setMeetupGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isMeetupLocked) {
      loadMeetupGroups();
    }
  }, [isMeetupLocked, loadMeetupGroups]);

  useEffect(() => {
    if (isEditing && campaignId && meetupConnected) {
      fetch(`/api/meetup/events/${campaignId}`)
        .then(res => res.json())
        .then(data => {
          const links = data.links || [];
          setExistingLinks(links);
          setSelectedGroups(links.map((l: MeetupEventLink) => l.meetupGroupUrlname));
        })
        .catch(() => {});
    }
  }, [isEditing, campaignId, meetupConnected]);

  const loadLumaCalendars = useCallback(async () => {
    setLumaCalendarsLoading(true);
    try {
      const res = await fetch('/api/luma/calendars');
      const data = await res.json();
      setLumaConnected(data.connected);
      setLumaCalendars(data.calendars || []);
    } catch {
      setLumaConnected(false);
    } finally {
      setLumaCalendarsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLumaLocked) {
      loadLumaCalendars();
    }
  }, [isLumaLocked, loadLumaCalendars]);

  useEffect(() => {
    if (isEditing && campaignId && lumaConnected) {
      fetch(`/api/luma/events/${campaignId}`)
        .then(res => res.json())
        .then(data => {
          const links = data.links || [];
          setExistingLumaLinks(links);
          setSelectedCalendars(links.map((l: LumaEventLink) => l.lumaCalendarId));
        })
        .catch(() => {});
    }
  }, [isEditing, campaignId, lumaConnected]);

  const toggleGroup = (urlname: string) => {
    setSelectedGroups(prev =>
      prev.includes(urlname)
        ? prev.filter(u => u !== urlname)
        : [...prev, urlname]
    );
  };

  const isGroupLinked = (urlname: string) =>
    existingLinks.some(l => l.meetupGroupUrlname === urlname);

  const handleCreateEvents = async () => {
    if (!campaignId || selectedGroups.length === 0) return;

    const newGroups = selectedGroups.filter(u => !isGroupLinked(u));
    if (newGroups.length === 0) {
      setMeetupMessage("All selected groups already have linked events.");
      return;
    }

    setMeetupSyncing(true);
    setMeetupError("");
    setMeetupMessage("");
    try {
      const res = await fetch('/api/meetup/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId, groupUrlnames: newGroups }),
      });
      const data = await res.json();
      if (data.errors?.length) {
        setMeetupError(data.errors.join('; '));
      }
      if (data.created > 0) {
        setMeetupMessage(`Created ${data.created} Meetup event${data.created > 1 ? 's' : ''}.`);
        const linksRes = await fetch(`/api/meetup/events/${campaignId}`);
        const linksData = await linksRes.json();
        setExistingLinks(linksData.links || []);
      }
    } catch {
      setMeetupError("Failed to create Meetup events");
    } finally {
      setMeetupSyncing(false);
    }
  };

  const handleSyncEvents = async () => {
    if (!campaignId) return;
    setMeetupSyncing(true);
    setMeetupError("");
    setMeetupMessage("");
    try {
      const res = await fetch('/api/meetup/events', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId }),
      });
      const data = await res.json();
      if (data.errors?.length) {
        setMeetupError(data.errors.join('; '));
      }
      if (data.synced > 0) {
        setMeetupMessage(`Synced ${data.synced} Meetup event${data.synced > 1 ? 's' : ''}.`);
      }
    } catch {
      setMeetupError("Failed to sync Meetup events");
    } finally {
      setMeetupSyncing(false);
    }
  };

  const toggleCalendar = (apiId: string) => {
    setSelectedCalendars(prev =>
      prev.includes(apiId)
        ? prev.filter(c => c !== apiId)
        : [...prev, apiId]
    );
  };

  const isCalendarLinked = (apiId: string) =>
    existingLumaLinks.some(l => l.lumaCalendarId === apiId);

  const handleCreateLumaEvents = async () => {
    if (!campaignId || selectedCalendars.length === 0) return;

    const newCalendars = selectedCalendars.filter(c => !isCalendarLinked(c));
    if (newCalendars.length === 0) {
      setLumaMessage("All selected calendars already have linked events.");
      return;
    }

    setLumaSyncing(true);
    setLumaError("");
    setLumaMessage("");
    try {
      const res = await fetch('/api/luma/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId, calendarApiIds: newCalendars }),
      });
      const data = await res.json();
      if (data.errors?.length) {
        setLumaError(data.errors.join('; '));
      }
      if (data.created > 0) {
        setLumaMessage(`Created ${data.created} Luma event${data.created > 1 ? 's' : ''}.`);
        const linksRes = await fetch(`/api/luma/events/${campaignId}`);
        const linksData = await linksRes.json();
        setExistingLumaLinks(linksData.links || []);
      }
    } catch {
      setLumaError("Failed to create Luma events");
    } finally {
      setLumaSyncing(false);
    }
  };

  const handleSyncLumaEvents = async () => {
    if (!campaignId) return;
    setLumaSyncing(true);
    setLumaError("");
    setLumaMessage("");
    try {
      const res = await fetch('/api/luma/events', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId }),
      });
      const data = await res.json();
      if (data.errors?.length) {
        setLumaError(data.errors.join('; '));
      }
      if (data.synced > 0) {
        setLumaMessage(`Synced ${data.synced} Luma event${data.synced > 1 ? 's' : ''}.`);
      }
    } catch {
      setLumaError("Failed to sync Luma events");
    } finally {
      setLumaSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Integrations</h2>
      <p className="text-sm text-gray-500">
        Connect third-party services to enhance your campaign. These are optional and can be configured later.
      </p>

      {/* Mailchimp */}
      <div className={`border rounded-lg p-5 ${isMailchimpLocked ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden ${isMailchimpLocked ? 'grayscale' : ''}`}>
            <Image
              src={INTEGRATIONS.mailchimp.logo}
              alt={`${INTEGRATIONS.mailchimp.label} logo`}
              width={40}
              height={40}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900">{INTEGRATIONS.mailchimp.label}</h3>
            <p className="text-xs text-gray-500">{INTEGRATIONS.mailchimp.description}</p>
          </div>
          {isMailchimpLocked && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Requires {mailchimpConfig.requiredPlan}
            </span>
          )}
        </div>

        {isMailchimpLocked ? (
          <p className="text-sm text-gray-400">
            Upgrade to {mailchimpConfig.requiredPlan} to connect Mailchimp and automatically add participants to your audiences.{" "}
            <Link href="/dashboard/upgrade" className="text-blue-500 hover:underline">
              Upgrade now
            </Link>
          </p>
        ) : (!audiencesLoading && audiences.length === 0) || audiencesError?.includes('not configured') ? (
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              Connect your Mailchimp account in{" "}
              <a href="/dashboard/settings?tab=integrations&integration=mailchimp" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                organization settings
              </a>{" "}
              to automatically add participants to your audiences.
            </p>
          </div>
        ) : (
          <div>
            <Label htmlFor="mailchimpAudienceId">Audience</Label>
            {audiencesLoading ? (
              <div className="mt-1 p-2 text-sm text-gray-500 bg-gray-50 rounded-md">
                Loading audiences...
              </div>
            ) : audiencesError ? (
              <div className="mt-1">
                <div className="p-2 text-sm text-red-600 bg-red-50 rounded-md mb-2">
                  {audiencesError}
                </div>
                <Button
                  type="button"
                  variant={secondaryButtonStyle ? undefined : "outline"}
                  size="sm"
                  style={secondaryButtonStyle}
                  onClick={onRefetchAudiences}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <Select
                id="mailchimpAudienceId"
                value={campaignData.mailchimpAudienceId}
                onChange={(e) => onCampaignChange("mailchimpAudienceId", e.target.value)}
                className="mt-1"
              >
                <SelectItem value="">Select an audience...</SelectItem>
                {audiences.map((audience) => (
                  <SelectItem key={audience.id} value={audience.id}>
                    {audience.name} ({audience.memberCount.toLocaleString()} members)
                  </SelectItem>
                ))}
              </Select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Participants will be automatically added to this audience when they join.
            </p>
          </div>
        )}
      </div>

      {/* Meetup */}
      <div className={`border rounded-lg p-5 ${isMeetupLocked ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden ${isMeetupLocked ? 'grayscale' : ''}`}>
            <Image
              src={INTEGRATIONS.meetup.logo}
              alt={`${INTEGRATIONS.meetup.label} logo`}
              width={40}
              height={40}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900">{INTEGRATIONS.meetup.label}</h3>
            <p className="text-xs text-gray-500">Create and sync Meetup events from your campaigns</p>
          </div>
          {isMeetupLocked && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Requires {meetupConfig.requiredPlan}
            </span>
          )}
        </div>

        {isMeetupLocked ? (
          <p className="text-sm text-gray-400">
            Upgrade to {meetupConfig.requiredPlan} to sync campaigns to Meetup.{" "}
            <Link href="/dashboard/upgrade" className="text-blue-500 hover:underline">
              Upgrade now
            </Link>
          </p>
        ) : !meetupConnected ? (
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              Connect your Meetup account in{" "}
              <a href="/dashboard/settings?tab=integrations" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                organization settings
              </a>{" "}
              to enable event syncing.
            </p>
          </div>
        ) : meetupGroupsLoading ? (
          <div className="p-2 text-sm text-gray-500 bg-gray-50 rounded-md">
            Loading Meetup groups...
          </div>
        ) : meetupGroups.length === 0 ? (
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              No Meetup groups found where you are an organizer. You need organizer permissions to create events.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <Label>Select Meetup groups to sync this campaign to</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {meetupGroups.map((group) => {
                const linked = isGroupLinked(group.urlname);
                return (
                  <label
                    key={group.urlname}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedGroups.includes(group.urlname)
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group.urlname)}
                      onChange={() => toggleGroup(group.urlname)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{group.name}</p>
                      <p className="text-xs text-gray-500">
                        {group.city}{group.state ? `, ${group.state}` : ''} &middot; {group.memberCount.toLocaleString()} members
                      </p>
                    </div>
                    {linked && (
                      <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Linked
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            {meetupError && (
              <div className="p-2 text-sm text-red-600 bg-red-50 rounded-md">{meetupError}</div>
            )}
            {meetupMessage && (
              <div className="p-2 text-sm text-green-700 bg-green-50 rounded-md">{meetupMessage}</div>
            )}

            {campaignId && (
              <div className="flex gap-2 pt-2">
                {selectedGroups.some(u => !isGroupLinked(u)) && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateEvents}
                    disabled={meetupSyncing}
                    style={secondaryButtonStyle}
                  >
                    {meetupSyncing ? "Creating..." : "Create Meetup Events"}
                  </Button>
                )}
                {existingLinks.length > 0 && (
                  <Button
                    type="button"
                    variant={secondaryButtonStyle ? undefined : "outline"}
                    size="sm"
                    onClick={handleSyncEvents}
                    disabled={meetupSyncing}
                    style={secondaryButtonStyle ? undefined : undefined}
                  >
                    {meetupSyncing ? "Syncing..." : "Sync Now"}
                  </Button>
                )}
              </div>
            )}

            {!campaignId && (
              <p className="text-xs text-gray-500">
                Save the campaign first to create Meetup events. Events will be created when you publish or save as draft.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Luma */}
      <div className={`border rounded-lg p-5 ${isLumaLocked ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden ${isLumaLocked ? 'grayscale' : ''}`}>
            <Image
              src={INTEGRATIONS.luma.logo}
              alt={`${INTEGRATIONS.luma.label} logo`}
              width={40}
              height={40}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900">{INTEGRATIONS.luma.label}</h3>
            <p className="text-xs text-gray-500">Create and sync Luma events from your campaigns</p>
          </div>
          {isLumaLocked && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Requires {lumaConfig.requiredPlan}
            </span>
          )}
        </div>

        {isLumaLocked ? (
          <p className="text-sm text-gray-400">
            Upgrade to {lumaConfig.requiredPlan} to sync campaigns to Luma.{" "}
            <Link href="/dashboard/upgrade" className="text-blue-500 hover:underline">
              Upgrade now
            </Link>
          </p>
        ) : !lumaConnected ? (
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              Connect your Luma account in{" "}
              <a href="/dashboard/settings?tab=integrations&integration=luma" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                organization settings
              </a>{" "}
              to enable event syncing.
            </p>
          </div>
        ) : lumaCalendarsLoading ? (
          <div className="p-2 text-sm text-gray-500 bg-gray-50 rounded-md">
            Loading Luma calendars...
          </div>
        ) : lumaCalendars.length === 0 ? (
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              No Luma calendars found. Create a calendar on Luma first.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <Label>Select Luma calendars to sync this campaign to</Label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {lumaCalendars.map((calendar) => {
                const linked = isCalendarLinked(calendar.api_id);
                return (
                  <label
                    key={calendar.api_id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedCalendars.includes(calendar.api_id)
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCalendars.includes(calendar.api_id)}
                      onChange={() => toggleCalendar(calendar.api_id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{calendar.name}</p>
                      {(calendar.geo_city || calendar.geo_region) && (
                        <p className="text-xs text-gray-500">
                          {calendar.geo_city}{calendar.geo_region ? `, ${calendar.geo_region}` : ''}
                        </p>
                      )}
                    </div>
                    {linked && (
                      <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Linked
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            {lumaError && (
              <div className="p-2 text-sm text-red-600 bg-red-50 rounded-md">{lumaError}</div>
            )}
            {lumaMessage && (
              <div className="p-2 text-sm text-green-700 bg-green-50 rounded-md">{lumaMessage}</div>
            )}

            {campaignId && (
              <div className="flex gap-2 pt-2">
                {selectedCalendars.some(c => !isCalendarLinked(c)) && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateLumaEvents}
                    disabled={lumaSyncing}
                    style={secondaryButtonStyle}
                  >
                    {lumaSyncing ? "Creating..." : "Create Luma Events"}
                  </Button>
                )}
                {existingLumaLinks.length > 0 && (
                  <Button
                    type="button"
                    variant={secondaryButtonStyle ? undefined : "outline"}
                    size="sm"
                    onClick={handleSyncLumaEvents}
                    disabled={lumaSyncing}
                  >
                    {lumaSyncing ? "Syncing..." : "Sync Now"}
                  </Button>
                )}
              </div>
            )}

            {!campaignId && (
              <p className="text-xs text-gray-500">
                Save the campaign first to create Luma events. Events will be created when you publish or save as draft.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
